import React, { useEffect, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../state/state';
import {
    Box,
    CircularProgress,
    Chip,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
} from '@mui/material';
import { Download, ReceiptLong, NotificationsActive, Inbox } from '@mui/icons-material';
import { collection, onSnapshot, orderBy, query, Timestamp } from 'firebase/firestore';
import { db } from '../../../../Config/firebase';
import { selectEffectiveUID } from '../../../../state/UserStatesV2/userSliceV2.ts';
import { openModal } from '../../../../state/ModalSlice/ModalSlice';
import { mobileStyles, cn } from '../../constants/mobileStyles';

interface BulkRequest {
    id: string;
    status: 'pending' | 'completed' | 'failed';
    type?: string;
    requestedAt?: Timestamp;
    completedAt?: Timestamp;
    sentCount?: number;
    failedCount?: number;
    doc_url?: string;
    error?: string;
}

const formatTimestamp = (ts?: Timestamp): string => {
    if (!ts) return 'N/A';
    try {
        return ts.toDate().toLocaleString('en-IN', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    } catch {
        return String(ts);
    }
};

const getRowStyle = (status: string) => {
    switch (status?.toLowerCase()) {
        case 'completed': return { backgroundColor: 'rgba(76, 175, 80, 0.06)' };
        case 'pending':   return { backgroundColor: 'rgba(255, 193, 7, 0.06)' };
        case 'failed':    return { backgroundColor: 'rgba(244, 67, 54, 0.06)' };
        default:          return {};
    }
};

const StatusChip: React.FC<{ status: string }> = ({ status }) => {
    const lower = status?.toLowerCase();
    const config: Record<string, { color: 'success' | 'warning' | 'error' | 'default'; label: string }> = {
        completed: { color: 'success', label: 'Completed' },
        pending:   { color: 'warning', label: 'Sending' },
        failed:    { color: 'error',   label: 'Failed' },
    };
    const { color, label } = config[lower] ?? { color: 'default', label: status ?? '—' };
    return (
        <Chip
            label={label}
            color={color}
            size="small"
            sx={{ fontWeight: 600, fontSize: '0.7rem', letterSpacing: '0.02em' }}
        />
    );
};

const TypeBadge: React.FC<{ type?: string }> = ({ type }) => {
    if (!type) return <span className="text-gray-400 text-sm">—</span>;
    const lower = type.toLowerCase();
    const isInvoice  = lower.includes('invoice');
    const isReminder = lower.includes('reminder');

    if (isInvoice) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 text-xs font-semibold border border-blue-200">
                <ReceiptLong sx={{ fontSize: '0.875rem' }} />
                Invoice
            </span>
        );
    }
    if (isReminder) {
        return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-50 text-purple-700 text-xs font-semibold border border-purple-200">
                <NotificationsActive sx={{ fontSize: '0.875rem' }} />
                Reminder
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-semibold border border-gray-200">
            {type.charAt(0).toUpperCase() + type.slice(1)}
        </span>
    );
};

const BulkRequestsPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const providerId = useAppSelector(selectEffectiveUID);
    const [requests, setRequests] = useState<BulkRequest[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!providerId) return;

        const colRef = collection(db, `ServiceProviders/${providerId}/BulkRequests`);
        const q = query(colRef, orderBy('requestedAt', 'desc'));

        const unsub = onSnapshot(
            q,
            (snap) => {
                const docs: BulkRequest[] = snap.docs.map((d) => ({
                    id: d.id,
                    ...(d.data() as Omit<BulkRequest, 'id'>),
                }));
                setRequests(docs);
                setLoading(false);
            },
            () => setLoading(false)
        );

        return () => unsub();
    }, [providerId]);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="300px">
                <div className="flex flex-col items-center gap-3">
                    <CircularProgress size={36} />
                    <p className="text-sm text-gray-500">Loading bulk requests…</p>
                </div>
            </Box>
        );
    }

    return (
        <div className={mobileStyles.padding.container}>
            <Box>
                {/* ── Header ── */}
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 my-4 sm:my-8">
                    <div>
                        <div className="flex items-center gap-2">
                            <h1 className={cn(mobileStyles.text.h2)}>Bulk Requests</h1>
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-50 text-green-600 text-xs font-semibold border border-green-200">
                                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                                Live
                            </span>
                        </div>
                        <p className={cn(mobileStyles.text.body, 'text-gray-500 mt-1')}>
                            Track all your bulk invoice and reminder jobs in one place.
                        </p>
                    </div>

                </div>

                {/* ── Action Buttons ── */}
                <div className="flex flex-wrap gap-3 mb-6">
                    <button
                        onClick={() => dispatch(openModal('BulkRaiseInvoiceModalOpen'))}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cc_brand hover:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
                    >
                        <ReceiptLong sx={{ fontSize: '1.1rem' }} />
                        Bulk Invoicing
                    </button>
                    <button
                        onClick={() => dispatch(openModal('BulkRemindersModalOpen'))}
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cc_brand hover:bg-blue-700 text-white text-sm font-semibold transition-colors shadow-sm"
                    >
                        <NotificationsActive sx={{ fontSize: '1.1rem' }} />
                        Bulk Reminders
                    </button>
                </div>

                {/* ── Table or Empty state ── */}
                {requests.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400 border border-dashed border-gray-200 rounded-2xl bg-gray-50">
                        <Inbox sx={{ fontSize: '3rem', opacity: 0.4 }} />
                        <p className="text-base font-semibold mt-3 text-gray-500">No bulk requests yet</p>
                        <p className="text-sm mt-1 text-gray-400 max-w-xs text-center">
                            Bulk invoices and reminders will appear here automatically once submitted.
                        </p>
                    </div>
                ) : (
                    <TableContainer
                        component={Paper}
                        elevation={0}
                        sx={{
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            maxHeight: { xs: '65vh', sm: '72vh', md: '78vh' },
                            overflowX: 'auto',
                            overflowY: 'auto',
                        }}
                    >
                        <Table sx={{ minWidth: 650 }} size="small">
                            <TableHead>
                                <TableRow sx={{ backgroundColor: '#f9fafb' }}>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Requested At</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Type</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Status</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Sent</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase' }}>Report</TableCell>
                                    <TableCell sx={{ fontWeight: 700, fontSize: '0.75rem', color: '#6b7280', whiteSpace: 'nowrap', letterSpacing: '0.05em', textTransform: 'uppercase', display: { xs: 'none', lg: 'table-cell' } }}>Request ID</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {requests.map((req) => (
                                    <TableRow
                                        key={req.id}
                                        style={getRowStyle(req.status)}
                                        sx={{ '&:hover': { backgroundColor: 'rgba(0,0,0,0.02)' }, transition: 'background 0.15s' }}
                                    >
                                        {/* Requested At */}
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <span className="text-sm text-gray-700 font-medium">{formatTimestamp(req.requestedAt)}</span>
                                            {req.completedAt && (
                                                <p className="text-xs text-gray-400 mt-0.5">
                                                    Done {formatTimestamp(req.completedAt)}
                                                </p>
                                            )}
                                        </TableCell>

                                        {/* Type */}
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <TypeBadge type={req.type} />
                                        </TableCell>

                                        {/* Status */}
                                        <TableCell>
                                            <StatusChip status={req.status} />
                                        </TableCell>

                                        {/* Sent */}
                                        <TableCell>
                                            {req.status === 'pending' ? (
                                                <span className="text-gray-300 text-sm">—</span>
                                            ) : (
                                                <span className="text-sm font-semibold text-green-600">
                                                    {req.sentCount ?? '—'}
                                                </span>
                                            )}
                                        </TableCell>

                                        {/* Report / Download */}
                                        <TableCell>
                                            {req.doc_url ? (
                                                <Tooltip title="Download XLSX report" arrow>
                                                    <a
                                                        href={req.doc_url}
                                                        download={`bulk-export-${req.id}.xlsx`}
                                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-800 text-xs font-semibold border border-blue-200 transition-colors"
                                                    >
                                                        <Download sx={{ fontSize: '0.9rem' }} />
                                                        <span className="hidden sm:inline">Download</span>
                                                    </a>
                                                </Tooltip>
                                            ) : req.status === 'pending' ? (
                                                <div className="flex items-center gap-1.5 text-xs text-yellow-600">
                                                    <CircularProgress size={12} sx={{ color: 'inherit' }} />
                                                    <span className="hidden sm:inline">Sending…</span>
                                                </div>
                                            ) : req.status === 'failed' ? (
                                                <Tooltip title={req.error ?? 'Job failed'} arrow>
                                                    <span className="text-xs text-red-500 font-medium cursor-help">See error ↗</span>
                                                </Tooltip>
                                            ) : (
                                                <span className="text-xs text-gray-400">N/A</span>
                                            )}
                                        </TableCell>

                                        {/* Request ID */}
                                        <TableCell sx={{ display: { xs: 'none', lg: 'table-cell' } }}>
                                            <Tooltip title={req.id} arrow>
                                                <span className="text-xs font-mono text-gray-400 truncate max-w-[120px] block cursor-default">
                                                    {req.id}
                                                </span>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Box>
        </div>
    );
};

export default BulkRequestsPage;
